import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from '@edx/frontend-platform/i18n';
import IntlTelInput from 'intl-tel-input/reactWithUtils';
import 'intl-tel-input/styles';

// Browser/OS autofill can set the input's DOM value directly, without going
// through the library's per-keystroke "as-you-type" formatter. That formatter
// is only reliable for incremental typing, so autofill can leave the visible
// text out of sync with the number IntlTelInput actually parses (getNumber()).
// Watching for the :-webkit-autofill pseudo-class via this animation trick
// lets us detect that moment and force a resync (see handleAutofillDetected).
const AUTOFILL_ANIMATION_NAME = 'phoneFieldAutofillDetect';
const autofillDetectStyle = `
  @keyframes ${AUTOFILL_ANIMATION_NAME} { from {} to {} }
  input.phone-field-autofill-detect:-webkit-autofill {
    animation-name: ${AUTOFILL_ANIMATION_NAME};
  }
`;

export default function PhoneFields({ label, name, value, formFields, handleChange, onValidChange, registration }) {
  const intl = useIntl();
  const [validationError, setValidationError] = useState(null);
  const [isPhoneValid, setIsPhoneValid] = useState(null);
  const [touched, setTouched] = useState(false);
  const itiRef = useRef(null);

  // Forces the visible input text to be re-rendered from the same canonical
  // number that drives validation, so the display can never disagree with it.
  const syncDisplayWithNumber = () => {
    const iti = itiRef.current?.getInstance();
    const number = iti?.getNumber();
    if (iti && number) {
      iti.setNumber(number);
    }
  };

  useEffect(() => {
    // Check validity on mount and when registration state changes
    if (registration && touched && !isPhoneValid) {
      setValidationError(intl.formatMessage({ id: 'account.settings.field.phone.number.invalid', defaultMessage: 'Invalid phone number +380*********' }));
      onValidChange(false);
    }
  }, [registration]);

  const handleChangeNumber = (number) => {
    if (number === formFields.phone_number) {
      return;
    }
    handleChange({ target: { name: 'phone_number', value: number } });
  };

  // isValid is passed in directly for the number that was just parsed, so
  // the error message always reflects the current number - never a stale
  // isPhoneValid from a previous render (that mismatch used to leave a
  // stale error on screen when the same valid number was re-entered, since
  // handleChangeNumber's number === formFields.phone_number guard above
  // would skip clearing it).
  const handleChangeValidity = (isValid) => {
    setIsPhoneValid(isValid);
    onValidChange(isValid);
    if (touched) {
      setValidationError(isValid
        ? null
        : intl.formatMessage({ id: 'account.settings.field.phone.number.invalid', defaultMessage: 'Invalid phone number +380*********' }));
    }
  };

  const handleClick = (e) => {
    setTouched(true);
  };

  const handleBlur = (e) => {
    syncDisplayWithNumber();
    if (touched && !isPhoneValid) {
      setValidationError(intl.formatMessage({ id: 'account.settings.field.phone.number.invalid', defaultMessage: 'Invalid phone number +380*********' }));
      onValidChange(false);
    }
  };

  const handleAutofillDetected = (e) => {
    if (e.animationName === AUTOFILL_ANIMATION_NAME) {
      syncDisplayWithNumber();
    }
  };

  return (
    <div className="pgn__form-group tw-mb-[24px]">
      <div className="tw-block">
        <label className="tw-font-grotesk tw-text-[16px] tw-text-netural-1000 tw-leading-[21.12px] tw-font-medium tw-mb-[8px]">{label}</label>
        <div className={`pgn__form-control-decorator-group ${validationError ? 'pgn__form-control--error' : ''}`}>
          <style>{autofillDetectStyle}</style>
          <IntlTelInput
            ref={itiRef}
            onChangeNumber={handleChangeNumber}
            onChangeValidity={handleChangeValidity}
            initOptions={{
              separateDialCode: true,
              preferredCountries: ['ua'],
              initialCountry: 'auto',
              autoPlaceholder: 'off',
              excludeCountries: ['ru'],
              geoIpLookup(success, failure) {
                fetch('https://ipapi.co/json')
                  .then((res) => res.json())
                  .then((data) => { success(data.country_code); })
                  .catch(() => { failure(); });
              },
            }}
            inputProps={{
              name: `${name}_input`,
              // value: value,
              className: `form-control phone-field-autofill-detect${validationError ? ' form-control-error' : ''}`,
              onClick: (e) => { handleClick(e); },
              onBlur: (e) => { handleBlur(e); },
              onChange: (e) => { e.preventDefault(); },
              onAnimationStart: (e) => { handleAutofillDetected(e); },
            }}
          />
        </div>
        {validationError && <span className="pgn__form-control-description form-text-size pgn__form-text pgn__form-text-invalid">{validationError}</span>}
      </div>
    </div>
  );
}
